import { svgToIcon } from '../../../components/Toolbar/toolbarUtils';
import Tool, { type ToolArgsWithController } from '../../../tools/Tool';
import { getTableStructureFromElement } from '../TableStructure';
import TableUtils from '../TableUtils';
import { Editor, Transforms } from 'slate';
import deleteRowSvg from '../../../assets/icons/deleteRow.svg';

class ToolDeleteRow extends Tool {
  static toolName = 'Delete row';

  static renderToolbarButtonIcon(): JSX.Element {
    return svgToIcon(deleteRowSvg);
  }

  static isEnabled({ editor, controller }: ToolArgsWithController) {
    const currentTable = TableUtils.currentTable(editor);
    if (!currentTable) return false;

    const tableStructure = getTableStructureFromElement(
      editor,
      currentTable[0]
    );
    if (!tableStructure) return false;

    // Don't allow delete if there's only one row
    if (tableStructure.height === 1) return false;

    const tableSelection = controller.state.pluginState.tableSelection;
    if (!tableSelection) return false;

    if (tableSelection.multipleCellsSelected) {
      return tableSelection.height < tableStructure.height;
    }

    return true;
  }

  static action({ editor, controller }: ToolArgsWithController): void {
    if (!editor?.selection) return;

    const currentTable = TableUtils.currentTable(editor);
    if (!currentTable) return;

    const tableStructure = getTableStructureFromElement(
      editor,
      currentTable[0]
    );
    if (!tableStructure) return;
    if (tableStructure.height === 1) return;

    const tableSelection = controller.state.pluginState.tableSelection;
    if (!tableSelection) return;

    let fromIndex = -1;
    let toIndex = tableStructure.height;

    let selectedColumnIndex = 0;
    if (tableSelection.multipleCellsSelected) {
      const cellPaths = Array.from(tableSelection.selectedCellPaths);

      [fromIndex, toIndex, selectedColumnIndex] = cellPaths.reduce(
        ([min, max, colIndex], path) => {
          const cell = tableStructure.pathToCoordinates(path);
          if (!cell) return [min, max, colIndex];

          return [
            Math.min(min, cell.rowIndex),
            Math.max(max, cell.rowIndex),
            Math.min(colIndex, cell.columnIndex),
          ];
        },
        [toIndex, fromIndex, tableStructure.width]
      );
    } else {
      const path = TableUtils.currentCellPath(editor);
      if (!path) return;

      const cell = tableStructure.pathToCoordinates(path.slice(-2));
      if (!cell) return;

      selectedColumnIndex = cell.columnIndex;

      fromIndex = path[path.length - 2];
      toIndex = path[path.length - 2];
    }

    if (fromIndex < 0 || toIndex >= tableStructure.height) return;

    for (let i = toIndex; i >= fromIndex; i--) {
      TableUtils.deleteRow(editor, i);
    }

    const deletedLastRow = toIndex === tableStructure.height - 1;
    const newRowIndex = deletedLastRow ? fromIndex - 1 : fromIndex;

    // We need to interact with the updated table/structure
    TableUtils.withCurrentTableStructure(
      editor,
      (currentTable, tableStructure) => {
        const cellInfo = tableStructure.cell(selectedColumnIndex, newRowIndex);

        if (cellInfo) {
          Transforms.select(
            editor,
            Editor.point(editor, [...currentTable[1], ...cellInfo.relativePath])
          );
        }
      }
    );
  }
}

export default ToolDeleteRow;
